import { Keyboard } from "lucide-react";
import { Fragment, useState } from "react";
import { Link, useMatches } from "react-router";
import { KeyboardShortcutsModal } from "@/components/keyboard-shortcuts-modal";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGlobalShortcuts } from "@/hooks/use-keyboard-shortcuts";

interface RouteHandle {
	crumb?: string;
}

export function AppHeader() {
	const matches = useMatches();
	const [shortcutsOpen, setShortcutsOpen] = useState(false);
	const { toggleSidebar } = useSidebar();

	// Global keyboard shortcuts
	useGlobalShortcuts({
		onToggleSidebar: toggleSidebar,
		onOpenShortcutsHelp: () => setShortcutsOpen(true),
	});

	// Filter matches that have a crumb handle and map to crumb data
	const crumbs = matches
		.map((match) => {
			const handle = match.handle as RouteHandle | undefined;
			return {
				crumb: handle?.crumb,
				pathname: match.pathname,
			};
		})
		.filter((item): item is { crumb: string; pathname: string } => Boolean(item.crumb));

	return (
		<>
			<header
				className="flex h-16 shrink-0 items-center justify-between gap-2 border-b px-4"
				data-testid="header"
			>
				<div className="flex items-center gap-2">
					<SidebarTrigger className="-ml-1" data-testid="header-toggle-sidebar" />
					<Separator orientation="vertical" className="mr-2 h-4" />
					<Breadcrumb data-testid="header-breadcrumb">
						<BreadcrumbList>
							{crumbs.map((crumb, index) => {
								const isLast = index === crumbs.length - 1;

								return (
									<Fragment key={crumb.pathname}>
										<BreadcrumbItem>
											{isLast ? (
												<BreadcrumbPage>{crumb.crumb}</BreadcrumbPage>
											) : (
												<BreadcrumbLink asChild>
													<Link to={crumb.pathname}>{crumb.crumb}</Link>
												</BreadcrumbLink>
											)}
										</BreadcrumbItem>
										{!isLast && <BreadcrumbSeparator />}
									</Fragment>
								);
							})}
						</BreadcrumbList>
					</Breadcrumb>
				</div>

				<div className="flex items-center gap-1">
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="size-9"
									onClick={() => setShortcutsOpen(true)}
								>
									<Keyboard className="size-4" />
									<span className="sr-only">Atajos de teclado</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Atajos de teclado (Ctrl+/)</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
					<LanguageSwitcher />
				</div>
			</header>

			<KeyboardShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
		</>
	);
}
